// @ts-strict-ignore
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { secureRandom } from 'src/app/utils/functions/secure-random';
import { WINDOW } from 'src/app/utils/window.token';

@Injectable({
  providedIn: 'root'
})
export class LoadingStatusIndicatorService {
  private readonly activeSubject = new BehaviorSubject<boolean>(false);
  private readonly progressSubject = new BehaviorSubject<number>(0);
  private readonly processes = new Set<string>();
  private trickleTimeout: number;

  public constructor(@Inject(WINDOW) private readonly window: Window) {}

  public get progress$(): Observable<number> {
    return this.progressSubject.asObservable();
  }

  public get active$(): Observable<boolean> {
    return this.activeSubject.asObservable();
  }

  private get progress(): number {
    return this.progressSubject.getValue();
  }

  private get active(): boolean {
    return this.activeSubject.getValue();
  }

  private set progress(value: number) {
    if (value !== this.progressSubject.getValue()) {
      this.progressSubject.next(value);
    }
  }

  private set active(value: boolean) {
    if (value !== this.activeSubject.getValue()) {
      this.activeSubject.next(value);
    }
  }

  public start(processId: string): void {
    this.processes.add(processId);

    this.trickle();
  }

  public finish(processId: string): void {
    this.processes.delete(processId);
    if (this.processes.size === 0) {
      this.clearTrickleTimeout();
      this.progress = 1;
      this.active = false;
    }
  }

  private trickle(): void {
    const startValue = 0.005;
    const maxValue = 0.994;
    if (this.active) {
      const step = 0.025;
      const multiplierAdjustment = -0.5;
      const multiplier = Math.log(this.progress) * multiplierAdjustment;
      const increment = step * multiplier;
      this.progress = Math.min(maxValue, this.progress + increment);
    } else {
      this.progress = 0;
      this.active = true;
      this.progress = startValue;
    }
    // creates a random time for the next trickle
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const t = 400 * (1 - (secureRandom() * 0.8 - 0.4));
    if (this.progress < maxValue) {
      this.clearTrickleTimeout();
      this.trickleTimeout = this.window.setTimeout(() => {
        this.trickle();
      }, t);
    }
  }

  private clearTrickleTimeout(): void {
    this.window.clearTimeout(this.trickleTimeout);
  }
}
